import styled from 'styled-components'
import { screenSizes } from '@shipt/design-system-themes'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { type SecondaryStorytellingData } from '@/features/cms/components/CMS/types'
import { Display, Body } from '@shipt/design-system-typography'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { Column, Stack } from '@shipt/design-system-layouts'
import { ButtonLink } from '@/features/shared/elements/Link/Buttons'
import { classNames } from '@/features/shared/utils/classNames'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import { ReferenceRenderer } from '@/features/cms/components/CMS/ReferenceRenderer'
import { sanitizeHTML } from '@/features/shared/utils/sanitize'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { theme } from '@/features/shared/constants/shiptTheme'

/**
 * This component corresponds to the `secondary_storytelling` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/secondary_storytelling
 * - **Production** - https://cms.shipt.com/content-type/pool/secondary_storytelling
 */
export const SecondaryStorytelling = ({
  content_type_id,
  id,
  data: { heading, description, image, image_placement, hide_mobile_image, cta_text, references },
}: SecondaryStorytellingData) => {
  const { trackCMSElementClicked } = useCMSTrackingEvents()

  const handleOnClick = () => {
    trackCMSElementClicked({
      type: 'link',
      next_location: cta_text?.url,
      content: cta_text?.text,
    })
  }

  try {
    return (
      <GridLayoutContainer {...getCMSWrapperProps({ content_type_id, id })}>
        <Stack
          direction={{
            base: image_placement.endsWith('Mobile: Bottom') ? 'column-reverse' : 'column',
            md: image_placement.startsWith('Desktop: Right') ? 'row-reverse' : 'row',
          }}
          spacing="3xl"
          align="center"
          justify={{ md: 'center' }}
        >
          {!!image.src && (
            <ImageWrapper className={classNames(hide_mobile_image && 'hide')}>
              <Image {...image} alt={image.alt} style={{ height: '100%', width: '100%' }} />
            </ImageWrapper>
          )}
          <ContentWrapper spacing="xxl">
            <Heading size="sm" as="h2">
              {heading}
            </Heading>
            {!!description && (
              <Description
                dangerouslySetInnerHTML={{
                  __html: sanitizeHTML(description),
                }}
              />
            )}
            {cta_text?.text ? (
              <ButtonLink
                href={cta_text.url}
                onClick={handleOnClick}
                opensInNewTab={cta_text.open_in_new_tab}
              >
                {cta_text.text}
              </ButtonLink>
            ) : null}
            <ReferenceRenderer references={references} />
          </ContentWrapper>
        </Stack>
      </GridLayoutContainer>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

const ImageWrapper = styled.div`
  width: 100%;
  border-radius: 8px;
  overflow: hidden;

  @media ${screenSizes.tablet} {
    max-width: 50%;
  }

  @media ${screenSizes.max_mobile} {
    &.hide {
      display: none;
    }
  }
`

const ContentWrapper = styled(Column)`
  width: 100%;
  height: fit-content;
  text-align: center;
  align-items: center;

  @media ${screenSizes.tablet} {
    width: 50%;
    text-align: left;
    align-items: flex-start;
  }
`

const Heading = styled(Display)`
  white-space: pre-wrap;
`

const Description = styled(Body)`
  white-space: pre-wrap;

  a {
    color: ${theme.purple500};
  }
`
